import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import Modal from 'react-native-modal';
import { Icon, Text } from 'react-native-elements';
import FastImage from 'react-native-fast-image';

import { getTopImageUrl } from '../../lib/gallery';
import { trackGalleryView } from '../../lib/analytics';
import analytics from '../../lib/wrapAnalytics';
import Carousel, { DisplayType } from '../../components/Carousel';
import Divider from '../../components/Divider';
import { Gallery, Image as GalleryImage, Action } from '../../api/adserver';
import config from '../../lib/config';
import HTML from 'react-native-render-html';

export interface Props {
  item?: Gallery | null;
  onClose: () => void;
}

interface GalleryAction {
  id: string;
  title: string;
  icon: string | null;
  action: Action;
}

const GalleryOverlay: React.FC<Props> = ({ item, onClose }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (item) {
      trackGalleryView(item);
    }
  }, [item]);

  const windowDimensions = useWindowDimensions();

  const showClose = true;

  if (!item) {
    return null;
  }

  const handleOnClose = () => {
    setVisible(false);
    onClose && onClose();
  };

  let topImageUrl = getTopImageUrl(item);

  const hasGallery = item.images && item.images.length > 0;

  let GalleryCaroselImages: GalleryImage[] = [];
  if (hasGallery && item.images) {
    GalleryCaroselImages = item.images;
  }

  return (
    <Modal
      backdropColor={'#000000'}
      backdropOpacity={0.5}
      isVisible={visible}
      hideModalContentWhileAnimating
      style={styles.overlay}
      onBackdropPress={onClose}
    >
      {showClose && (
        <View style={styles.topOverlay}>
          <View style={styles.closeButtonContainer}>
            <TouchableOpacity onPress={handleOnClose}>
              <Icon
                tvParallaxProperties={null}
                activeOpacity={0.5}
                name={'close'}
                size={30}
                color={'#505050'}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <SafeAreaView>
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          style={styles.scrollView}
        >
          {topImageUrl && (
            <>
              <FastImage
                source={{ uri: topImageUrl }}
                resizeMode={FastImage.resizeMode.cover}
                style={styles.mainImage}
              />
              <Divider />
            </>
          )}
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            <Text style={styles.featureHeading}>{item.title}</Text>
            <HTML
              source={{ html: item.description }}
              contentWidth={windowDimensions.width}
            />
          </View>
          <Divider />
          {hasGallery && (
            <>
              <Divider />
              <View
                style={[
                  styles.featureHeadingContainer,
                  styles.sectionStart,
                  styles.screenEnd,
                ]}
              >
                <Text style={[styles.featureHeading, styles.subSectionHeading]}>
                  Gallery
                </Text>
                <Carousel
                  name="Gallerycarousel"
                  displayType={DisplayType.photo}
                  items={GalleryCaroselImages}
                />
              </View>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    margin: 0,
    marginTop: 40,
  },
  closeButtonContainer: {
    height: '100%',
    zIndex: 1000,
    flex: 1,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
    minHeight: '100%',
  },
  featureHeadingContainer: {
    marginBottom: 5,
    marginHorizontal: 15,
  },
  sectionStart: {
    marginTop: 20,
    marginBottom: 20,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
    marginBottom: 10,
  },
  subSectionHeading: {
    fontSize: 18,
    color: config.theme.advert.subSectionHeadingTextColor,
    fontWeight: '700',
  },
  mainImage: {
    height: 204,
    width: '100%',
  },
  detailText: {
    fontSize: 14,
  },
  bottomMargin1Em: {
    marginBottom: 14,
  },
  screenEnd: {
    marginBottom: 20,
  },
});

export default GalleryOverlay;
